/**
 * /inkoop — Inkoopregister: bij wie kopen we terugkerend voorraad, wat, hoe,
 * en wanneer voor het laatst. Leest /api/inkoop; bestellingen vastleggen kan
 * elke medewerker. Register bewerken (velden) gaat via de API (admin), nog
 * zonder formulier: dat is bewust, het register verandert zelden.
 */
import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Card } from '../components/ui/Card';
import { supabase } from '../lib/supabase';

interface Artikel {
  artikel: string;
  artikelnummer?: string;
  eenheid?: string;
  standaard_aantal?: number;
  prijs_excl?: number;
  prijs_eenheid?: string;
  prijs_peildatum?: string;
}

interface Bestelling {
  id: string;
  besteld_op: string;
  ordernummer: string | null;
  omschrijving: string | null;
  bedrag_excl: number | null;
  bron: string;
  besteld_door: string | null;
}

interface Leverancier {
  id: string;
  slug: string;
  naam: string;
  website: string | null;
  bestelwijze: 'online' | 'mail' | 'telefoon';
  bestel_email: string | null;
  contactpersoon: string | null;
  telefoon: string | null;
  klantnummer: string | null;
  wat: string;
  artikelen: Artikel[];
  ritme: string | null;
  signaal: string | null;
  bestelinstructie: string | null;
  betaling: string | null;
  onepassword_item: string | null;
  eigenaar: string | null;
  notities: string | null;
  actief: boolean;
  laatste_bestelling: Bestelling | null;
  dagen_geleden: number | null;
  bestellingen: Bestelling[];
}

async function officeFetch(path: string, method = 'GET', body?: unknown): Promise<unknown> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(path, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || `API error: ${response.status}`);
  }
  return response.json();
}

const euro = (n: number | null | undefined) =>
  n === null || n === undefined ? null : new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n);

const datumNl = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });

const vandaagIso = () => new Date().toISOString().slice(0, 10);

const BESTELWIJZE_LABEL: Record<Leverancier['bestelwijze'], string> = {
  online: 'Webshop met login',
  mail: 'Per e-mail',
  telefoon: 'Telefonisch',
};

function bestelMailto(l: Leverancier): string | null {
  if (l.bestelwijze !== 'mail' || !l.bestel_email) return null;
  const regels = l.artikelen
    .map((a) => `${a.artikel}${a.standaard_aantal ? ` x ${a.standaard_aantal}` : ''}`)
    .join('\n');
  const isKolckmann = l.slug === 'kolckmann';
  const subject = isKolckmann ? 'Carpet underlay order' : `Bestelling Cinco Cleaning${l.klantnummer ? ` (klantnr ${l.klantnummer})` : ''}`;
  const body = isKolckmann
    ? `Dear Mr Zuppardo,\n\nI would like to order the following carpet underlay:\n\n${regels}\n\nThank you. Looking forward to the confirmation.\n\nKind regards,\nCinco Cleaning B.V.`
    : `Beste,\n\nGraag bestellen wij${l.klantnummer ? ` (klantnummer ${l.klantnummer})` : ''}:\n\n${regels}\n\nAfleveradres: Cinco Cleaning B.V., Oeveraseweg 13a, 7971 PA Havelte.\n\nMet vriendelijke groet,\nCinco Cleaning B.V.`;
  return `mailto:${l.bestel_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function Veld({ label, children }: { label: string; children: React.ReactNode }) {
  if (!children) return null;
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-0.5 whitespace-pre-line text-sm text-gray-800">{children}</div>
    </div>
  );
}

function BestellingForm({ leverancier, onDone }: { leverancier: Leverancier; onDone: () => void }) {
  const [besteldOp, setBesteldOp] = useState(vandaagIso());
  const [ordernummer, setOrdernummer] = useState('');
  const [omschrijving, setOmschrijving] = useState(
    leverancier.artikelen.map((a) => `${a.standaard_aantal ?? ''}x ${a.artikel}`.trim()).join(', ')
  );
  const [bedrag, setBedrag] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await officeFetch('/api/inkoop?action=bestelling', 'POST', {
        leverancierId: leverancier.id,
        besteldOp,
        ordernummer: ordernummer || undefined,
        omschrijving: omschrijving || undefined,
        bedragExcl: bedrag || undefined,
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setBusy(false);
    }
  };

  const input = 'w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-gray-500 focus:outline-none';
  return (
    <form onSubmit={submit} className="mt-3 grid grid-cols-1 gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 sm:grid-cols-4">
      <label className="text-xs text-gray-600">Besteld op
        <input type="date" className={input} value={besteldOp} max={vandaagIso()} onChange={(e) => setBesteldOp(e.target.value)} required />
      </label>
      <label className="text-xs text-gray-600">Ordernummer
        <input className={input} value={ordernummer} onChange={(e) => setOrdernummer(e.target.value)} placeholder="optioneel" />
      </label>
      <label className="text-xs text-gray-600 sm:col-span-2">Wat
        <input className={input} value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} />
      </label>
      <label className="text-xs text-gray-600">Bedrag excl. btw
        <input className={input} value={bedrag} onChange={(e) => setBedrag(e.target.value)} placeholder="optioneel" inputMode="decimal" />
      </label>
      <div className="flex items-end gap-2 sm:col-span-3">
        <button type="submit" disabled={busy} className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">
          {busy ? 'Opslaan...' : 'Vastleggen'}
        </button>
        <button type="button" onClick={onDone} className="rounded-md px-3 py-1.5 text-sm text-gray-600">Annuleren</button>
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>
    </form>
  );
}

function LeverancierCard({ l, onChanged }: { l: Leverancier; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [logForm, setLogForm] = useState(false);
  const mailto = bestelMailto(l);

  return (
    <Card padding="none" className={clsx(!l.actief && 'opacity-60')}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-start justify-between gap-4 p-5 text-left">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">{l.naam}</h3>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{BESTELWIJZE_LABEL[l.bestelwijze]}</span>
            {l.eigenaar && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800">Bij afwezigheid: {l.eigenaar}</span>}
          </div>
          <p className="mt-1 text-sm text-gray-700">{l.wat}</p>
          {l.ritme && <p className="mt-1 text-xs text-gray-500">{l.ritme}</p>}
        </div>
        <div className="shrink-0 text-right text-sm">
          {l.laatste_bestelling ? (
            <>
              <div className="text-gray-900">{datumNl(l.laatste_bestelling.besteld_op)}</div>
              <div className="text-xs text-gray-500">{l.dagen_geleden} dagen geleden</div>
            </>
          ) : (
            <div className="text-xs text-gray-500">Nog niet besteld</div>
          )}
        </div>
      </button>

      {open && (
        <div className="space-y-4 border-t border-gray-100 p-5">
          <div className="flex flex-wrap gap-2">
            {l.website && (
              <a href={l.website} target="_blank" rel="noreferrer" className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50">
                Webshop openen
              </a>
            )}
            {mailto && (
              <a href={mailto} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50">
                Bestelmail openen
              </a>
            )}
            <button type="button" onClick={() => setLogForm(true)} className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white">
              Bestelling vastleggen
            </button>
          </div>
          {logForm && <BestellingForm leverancier={l} onDone={() => { setLogForm(false); onChanged(); }} />}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Veld label="Signaal: wanneer bestellen">{l.signaal}</Veld>
            <Veld label="Hoe bestellen">{l.bestelinstructie}</Veld>
            <Veld label="Betaling">{l.betaling}</Veld>
            <Veld label="Contact">
              {[l.contactpersoon, l.bestel_email, l.telefoon, l.klantnummer && `Klantnummer ${l.klantnummer}`].filter(Boolean).join('\n')}
            </Veld>
            <Veld label="Login">{l.onepassword_item ? `1Password: "${l.onepassword_item}" (vault Cinco Operatie)` : l.bestelwijze === 'online' ? 'Geen login bekend; als gast bestellen' : null}</Veld>
            <Veld label="Notities">{l.notities}</Veld>
          </div>

          {l.artikelen.length > 0 && (
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Standaardbestelling</div>
              <table className="mt-1 w-full text-sm">
                <tbody>
                  {l.artikelen.map((a, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="py-1 pr-3 text-gray-800">{a.artikel}{a.artikelnummer && <span className="ml-1 text-xs text-gray-400">{a.artikelnummer}</span>}</td>
                      <td className="py-1 pr-3 text-right text-gray-700 whitespace-nowrap">{a.standaard_aantal ? `${a.standaard_aantal} ${a.eenheid ?? ''}` : ''}</td>
                      <td className="py-1 text-right text-gray-500 whitespace-nowrap">
                        {a.prijs_excl !== undefined && `${euro(a.prijs_excl)}${a.prijs_eenheid ? ` ${a.prijs_eenheid}` : ''} excl.`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Logboek</div>
            {l.bestellingen.length === 0 ? (
              <p className="mt-1 text-sm text-gray-500">Nog geen bestellingen vastgelegd.</p>
            ) : (
              <table className="mt-1 w-full text-sm">
                <tbody>
                  {l.bestellingen.map((b) => (
                    <tr key={b.id} className="border-t border-gray-100">
                      <td className="py-1 pr-3 whitespace-nowrap text-gray-800">{datumNl(b.besteld_op)}</td>
                      <td className="py-1 pr-3 text-gray-700">{b.omschrijving}{b.ordernummer && <span className="ml-1 text-xs text-gray-400">{b.ordernummer}</span>}</td>
                      <td className="py-1 pr-3 text-right whitespace-nowrap text-gray-700">{euro(b.bedrag_excl)}</td>
                      <td className="py-1 text-right text-xs text-gray-400 whitespace-nowrap">{b.bron === 'mailscan' ? 'mailbox' : b.besteld_door ?? b.bron}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

export function InkoopPage() {
  const [leveranciers, setLeveranciers] = useState<Leverancier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const laad = async () => {
    try {
      const data = (await officeFetch('/api/inkoop')) as { leveranciers: Leverancier[] };
      setLeveranciers(data.leveranciers);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void laad(); }, []);

  const gesorteerd = useMemo(
    () => [...leveranciers].sort((a, b) => Number(b.actief) - Number(a.actief) || (b.dagen_geleden ?? 9999) - (a.dagen_geleden ?? 9999)),
    [leveranciers]
  );

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto max-w-4xl space-y-4 p-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Inkoop</h1>
          <p className="mt-1 text-sm text-gray-600">
            Terugkerende voorraad: bij wie, wat, hoe vaak en hoe. Leg elke bestelling vast, dan weet de volgende wanneer het weer tijd is.
            Dropship voor de webshop (SEBO) loopt via Webshop; wasserijchemie en Vlekkenbaas via Albert.
          </p>
        </div>
        {loading && <p className="text-sm text-gray-500">Laden...</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {gesorteerd.map((l) => <LeverancierCard key={l.id} l={l} onChanged={laad} />)}
      </div>
    </div>
  );
}
